import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { handleDenialResponse } from './authDenial'

// ── GET dedupe / short-TTL cache ──────────────────────────────────────────────
// Shared across every useAuthFetch consumer. Collapses StrictMode double-
// fetches and page+map duplicate fetches of the same URL into one network
// call. Entries hold the ORIGINAL Response; every consumer (including the
// first) receives a clone, so bodies are never consumed twice.
// Any mutation (non-GET) clears the cache, so refetch-after-save is fresh.

const GET_CACHE_TTL_MS = 5_000
const getCache = new Map<string, { ts: number; promise: Promise<Response> }>()

/** Test/debug escape hatch — clears the shared GET cache. */
export function clearAuthFetchCache(): void {
  getCache.clear()
}

/**
 * Returns a fetch wrapper that automatically injects the Clerk Bearer token.
 *
 * The returned function is referentially STABLE for the component's lifetime
 * (empty useCallback deps; live Clerk state is read through refs). Effects
 * keyed on [authFetch] therefore run once instead of refiring every time
 * Clerk's getToken identity churns during mount (RC2 — the ~40-call storm).
 */
export function useAuthFetch() {
  const { getToken, isLoaded, userId, orgId } = useAuth()
  const identityRef = useRef('')
  identityRef.current = JSON.stringify([userId, orgId])

  // Live refs — the stable callback reads these at call time.
  const getTokenRef = useRef(getToken)
  const isLoadedRef = useRef(isLoaded)
  useEffect(() => {
    getTokenRef.current = getToken
    isLoadedRef.current = isLoaded
  })

  return useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const doFetch = async (): Promise<Response> => {
        // If Clerk hasn't finished loading, wait briefly (reads the live ref,
        // so it actually exits as soon as Clerk is ready — max 3s safety cap).
        if (!isLoadedRef.current) {
          await new Promise<void>((resolve) => {
            const start = Date.now()
            const interval = setInterval(() => {
              if (isLoadedRef.current || Date.now() - start > 3000) {
                clearInterval(interval)
                resolve()
              }
            }, 50)
          })
        }
        const token = await getTokenRef.current()
        const existing = (options.headers || {}) as Record<string, string>
        const headers: Record<string, string> = { ...existing }
        if (!headers['Content-Type']) headers['Content-Type'] = 'application/json'
        if (token) headers['Authorization'] = `Bearer ${token}`
        const res = await fetch(url, { ...options, headers })
        // Single-shot denial handling. Runs on EVERY network path (cached GET,
        // signal'd GET, mutation) since all route through doFetch. Reads a
        // clone, so the body the caller reads stays intact, and never throws —
        // a valid response is never turned into a parsing error. 402 → the
        // billing denials route to the API-driven /offboarded recovery page.
        if (!res.ok) await handleDenialResponse(res)
        return res
      }

      const method = (options.method ?? 'GET').toUpperCase()

      // Mutations invalidate everything and always hit the network.
      if (method !== 'GET') {
        getCache.clear()
        return doFetch()
      }

      // Caller-managed lifecycles (AbortController) bypass the shared cache —
      // an abort by one consumer must not reject another's request.
      if (options.signal) return doFetch()

      const cacheKey = `${identityRef.current}:${url}`
      const hit = getCache.get(cacheKey)
      if (hit && Date.now() - hit.ts < GET_CACHE_TTL_MS) {
        return hit.promise.then(r => r.clone())
      }
      // Sweep expired entries on the miss path — map pans mint a unique bbox
      // URL per fetch, so without eviction the cache grows for the session.
      for (const [key, entry] of getCache) {
        if (Date.now() - entry.ts >= GET_CACHE_TTL_MS) getCache.delete(key)
      }
      const promise = doFetch()
      getCache.set(cacheKey, { ts: Date.now(), promise })
      try {
        const res = await promise
        // Never let a transient 4xx/5xx poison reads for the TTL window —
        // only successful responses stay cached.
        if (!res.ok) getCache.delete(cacheKey)
        return res.clone()
      } catch (err) {
        getCache.delete(cacheKey)
        throw err
      }
    },
    []
  )
}
